import React from 'react'

import { ChevronDown } from 'lucide-react'
import { useHotkeys } from 'react-hotkeys-hook'

import { Button } from '~/components/component-library/Button'
import { ButtonShortcut } from '~/components/component-library/Button/components/ButtonShortcut'
import { buttonVariants } from '~/components/component-library/Button/styles'
import {
  type ButtonDropdownOption,
  type ButtonProps,
} from '~/components/component-library/Button/types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/components/component-library/Dropdown'
import { cn } from '~/utils/tailwind-utils'

type ButtonWithDropdownProps = ButtonProps & {
  dropdownOptions: ButtonDropdownOption[]
  ref?: React.RefObject<HTMLButtonElement | HTMLAnchorElement | null>
}

const TERTIARY_SEPARATOR_SHADOW =
  'shadow-[inset_1px_0_0_rgb(229,231,235)] dark:shadow-[inset_1px_0_0_rgb(31,41,55)]'

const separatorShadowByVariant: Partial<
  Record<NonNullable<ButtonProps['variant']>, string>
> = {
  primary:
    'shadow-[inset_1px_0_0_rgba(255,255,255,0.5)] dark:shadow-[inset_1px_0_0_rgba(0,0,0,0.5)]',
  tertiary: TERTIARY_SEPARATOR_SHADOW,
  nav: TERTIARY_SEPARATOR_SHADOW,
  danger: 'shadow-[inset_1px_0_0_rgba(255,255,255,0.5)]',
}

export const ButtonWithDropdown = ({
  ref,
  dropdownOptions,
  className,
  ...props
}: ButtonWithDropdownProps) => {
  const {
    variant = 'primary',
    size = 'base',
    shape,
    isDisabled,
    isLoading,
  } = props
  const isInert = isDisabled || isLoading

  return (
    <div className="inline-flex self-start">
      {dropdownOptions.map((option, index) =>
        option.shortcut ? (
          <DropdownOptionHotkey
            key={index}
            shortcut={option.shortcut}
            onClick={option.onClick}
            isDisabled={option.isDisabled || isInert}
          />
        ) : null,
      )}
      <Button
        {...props}
        className={cn(className, 'rounded-r-none')}
        ref={ref}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={isInert}
            aria-label="More options"
            className={cn(
              buttonVariants({
                variant,
                size,
                shape,
                isDisabled: isInert,
                isLoading,
              }),
              'rounded-l-none [&>svg]:my-0.5',
              size === 'small'
                ? 'px-1.5 [&>svg]:size-4'
                : 'px-2 [&>svg]:size-5',
              variant === 'secondary' && '-ml-px',
              separatorShadowByVariant[variant],
            )}
          >
            <ChevronDown />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-auto min-w-0">
          {dropdownOptions.map((option, index) => (
            <DropdownMenuItem
              key={index}
              onClick={option.onClick}
              disabled={option.isDisabled}
              tooltip={option.isDisabled ? option.disabledReason : undefined}
              className="gap-3"
            >
              <span>{option.label}</span>
              {option.shortcut && (
                <ButtonShortcut
                  shortcut={{ key: option.shortcut.key }}
                  variant="secondary"
                  fullWidth={false}
                  size="small"
                />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

const DropdownOptionHotkey = ({
  shortcut,
  onClick,
  isDisabled,
}: {
  shortcut: NonNullable<ButtonDropdownOption['shortcut']>
  onClick: () => void
  isDisabled?: boolean
}) => {
  useHotkeys(shortcut.key, onClick, {
    enabled: !isDisabled,
    enableOnFormTags:
      shortcut.enableOnFormTags || shortcut.key === 'meta+enter'
        ? ['INPUT', 'TEXTAREA', 'SELECT']
        : undefined,
  })
  return null
}
